/** @jsxImportSource @emotion/react */

import React, { Suspense, useEffect, useMemo } from 'react';
// @ts-ignore
import reactDatepickerStyles from 'react-datepicker/dist/react-datepicker.css?raw';
import * as locales from 'date-fns/locale'; // TODO: this could probably be optimized to reduce bundle size
import { useTranslation } from '../../../contexts/TranslationContext/TranslationContext';
import { useTheme } from '../../ThemeProvider/ThemeProvider';
import { Dropdown } from '../Dropdown/Dropdown';
import { LinearLayout } from '../LinearLayout/LinearLayout';
import { Button } from '../Button/Button';
import { CSSProp } from '../../../types/emotion';
import { lazily } from 'react-lazily';

const ReactDatePickerPromise = import('react-datepicker'); // preload
const { default: ReactDatePicker } = lazily(() => ReactDatePickerPromise);

export interface DatePickerProps {
  /**
   * Date value
   */
  date: Date | null;
  /**
   * On change handler
   */
  onChange?: (date: Date | null) => void;
  /**
   * enable selecting a range of dates
   */
  selectRange?: boolean;
  /**
   * End date value
   */
  endDate?: Date | null;
  /**
   * On end date change handler
   */
  onEndDateChange?: (date: Date | null) => void;
  /**
   * Year range for the dropdown
   */
  yearRange?: [number, number];
  /**
   * Additional props for the date picker
   */
  datePickerProps?: any;
}

export function DatePicker({
  date,
  onChange,
  selectRange = false,
  endDate,
  onEndDateChange,
  yearRange = [2000, new Date().getFullYear()],
  datePickerProps,
  ...props
}: DatePickerProps & Omit<Partial<React.HTMLAttributes<HTMLDivElement>>, keyof DatePickerProps> & CSSProp) {
  const { t, lang } = useTranslation();
  const { tokens } = useTheme();

  useEffect(() => {
    (async () => {
      const { registerLocale } = await ReactDatePickerPromise;
      registerLocale(lang, locales[lang as keyof typeof import('date-fns/locale')]);
    })();
  });

  const years = useMemo(
    () =>
      Array.from({ length: yearRange[1] - yearRange[0] + 1 }, (_, i) => yearRange[0] + i).map(e => ({
        id: e.toString(),
        displayName: e.toString(),
      })),
    [yearRange],
  );
  const months = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => ({
        id: i.toString(),
        displayName: new Date(0, i).toLocaleString(lang, { month: 'long' }),
      })),
    [lang],
  );

  return (
    <div
      css={{
        '& .react-datepicker': {
          border: 'none',
        },
        '& .react-datepicker__header': {
          background: 'none',
          border: 'none',
          '& label': {
            textAlign: 'left',
          },
        },
        '& .react-datepicker__day-names': {
          fontWeight: 'bold',
          fontSize: '0.75rem',
          lineHeight: '1.125rem',
        },
        '& .react-datepicker__month': {
          fontSize: '1rem',
          lineHeight: '1.5rem',
        },
        '& .react-datepicker__day-name': {
          margin: '0.25rem',
        },
        '& .react-datepicker__day': {
          margin: '0.25rem',
        },
        '& .react-datepicker__day--outside-month': {
          color: tokens.colors['bosch-gray-55'],
        },
        '& .react-datepicker__day--selected, & .react-datepicker__day--in-range': {
          color: tokens.colors['bosch-white'],
          backgroundColor: tokens.colors['bosch-blue-50'],
        },
        '& .react-datepicker__day--in-selecting-range': {
          color: tokens.colors['bosch-white'],
          backgroundColor: tokens.colors['bosch-blue-60'],
        },
      }}
      {...props}
    >
      <style>{reactDatepickerStyles}</style>
      <Suspense fallback={null}>
        <ReactDatePicker
          locale={lang}
          selected={date}
          onChange={(date: [Date | null, Date | null]) => {
            if (selectRange) {
              onChange?.(date[0]);
              onEndDateChange?.(date[1]);
            } else if (date) {
              onChange?.(date as any);
            }
          }}
          inline
          {...((selectRange ? { selectsRange: true, startDate: date, endDate } : {}) as unknown as {
            selectsRange: true;
          })}
          renderCustomHeader={({
            date,
            changeYear,
            changeMonth,
            decreaseMonth,
            increaseMonth,
            prevMonthButtonDisabled,
            nextMonthButtonDisabled,
          }: any) => (
            <LinearLayout orientation="horizontal" spacing="tiny" align="center" css={{ marginBottom: '1rem' }}>
              <Button variant="icononly" icon="back-left" onClick={decreaseMonth} disabled={prevMonthButtonDisabled} />
              <Dropdown
                value={date.getMonth().toString()}
                onChange={v => changeMonth(parseInt(v as string))}
                options={months}
                label={t('DSSF.Components.Core.DatePicker.Month')}
              />
              <Dropdown
                value={date.getFullYear().toString()}
                onChange={v => changeYear(parseInt(v as string))}
                options={years}
                label={t('DSSF.Components.Core.DatePicker.Year')}
              />
              <Button
                variant="icononly"
                icon="forward-right"
                onClick={increaseMonth}
                disabled={nextMonthButtonDisabled}
              />
            </LinearLayout>
          )}
          {...datePickerProps}
        />
      </Suspense>
    </div>
  );
}
